import { Box } from "@chakra-ui/react";
import {
  Dict,
  getAccountFilterFromQuery,
  getObjectKeys,
  getOpportunityFilterQuery,
  ModelId,
  VeriTabPanel,
  VeriTabs,
} from "@verivest/shared-components";
import { useVeriToast } from "@verivest/shared-components/";
import useDocumentModel from "@verivest/shared-components/src/api/hooks/models/useDocumentModel";
import { Card } from "@verivest/shared-components/src/components/dashboard";
import { VeriAsyncRouteComponentType } from "@verivest/shared-components/src/defines/after-js-types";
import { logError } from "@verivest/shared-components/src/error-handling/logError";
import useGetDataOrDefaults from "@verivest/shared-components/src/hooks/data/useGetDataOrDefaults";
import { useWorkerJobs } from "@verivest/shared-components/src/hooks/ui/useWorkerJobs/useWorkerJobs";
import { useVeriForm } from "@verivest/shared-components/src/react-hook-form";
import React from "react";
import { InvestorPortalWhiteLabelPageContainer } from "../../../components/investor-portal/layouts/InvestorPortalWhiteLabelPageContainer";
import { DocumentsEmptyStatusView } from "../../../components/investor-portal/white-label/documents/DocumentsEmptyStatusView";
import {
  DocumentsTable,
  DocumentsTableHookFormFiltersData,
} from "../../../components/investor-portal/white-label/documents/DocumentsTable";
import { ManagerWhiteLabelRouteParams } from "../../../routes/investorPortalPages.types";
import { useDocumentsTags } from "./documents-page/useDocumentsTags";
import {
  ManagerWhiteLabelRoutesServerInitialProps,
  WhiteLabelPageExtraProps,
} from "./types";
import { useIsMobile } from "../../../hooks/useIsMobile";

export const DocumentsPage: VeriAsyncRouteComponentType<
  ManagerWhiteLabelRoutesServerInitialProps & WhiteLabelPageExtraProps,
  ManagerWhiteLabelRouteParams
> = (props) => {
  const isMobile = useIsMobile();
  const urlParams = new URLSearchParams(props.location.search);
  const selectedFilterOpportunity = getOpportunityFilterQuery(urlParams);
  const selectedFilterAccount = getAccountFilterFromQuery(urlParams);

  const toast = useVeriToast();
  const form = useVeriForm<DocumentsTableHookFormFiltersData>(
    selectedFilterOpportunity || selectedFilterAccount
      ? {
          defaultValues: {
            opportunity: selectedFilterOpportunity,
            account: selectedFilterAccount,
          },
        }
      : undefined,
  );
  const documentModel = useDocumentModel();
  const { appendJob } = useWorkerJobs();

  let availableTags = useDocumentsTags();
  availableTags = { "All documents": undefined, ...availableTags };

  const filterOpportunity = form.watch("opportunity");
  const filterAccount = form.watch("account");
  const filterQuery = form.watch("searchQuery");
  const dates = String(form.watch("date") || "")
    .split(",")
    .filter((date) => date);

  const hasAnyFilter =
    !!filterOpportunity ||
    !!filterAccount ||
    !!filterQuery ||
    dates?.length > 0;

  const { data: checkDocument } = useGetDataOrDefaults(
    undefined,
    async () =>
      (
        await documentModel.getMany(null, {
          manager_id: props.manager?.id || "",
          "page[size]": 1,
        })
      )?.data || [],
  );

  const categories = availableTags ? getObjectKeys(availableTags) : [];

  const onClearFilters = () => {
    form.reset({ searchQuery: "" });
  };

  const startDownloadJob = async (ids: number[]) => {
    try {
      const result = await documentModel.startZipDownloadJob(
        ids,
        Number(props.manager?.id),
      );

      if (result.workflowJobId) {
        appendJob({
          id: result.workflowJobId,
          title: "Generating documents archive",
          description: `of ${ids.length} documents`,
          checkInterval: 3000,
          onCompleted: (result) => {
            toast.toastSuccess("Documents archive is ready to download");
            const output = result.outputs as unknown as Dict;
            if (output?.generateZipArchive?.fileName) {
              documentModel.downloadZipDocuments(
                output.generateZipArchive.fileName,
              );
            }
          },
        });
      }
    } catch (err) {
      logError(err);
    }
  };

  if (!availableTags) return <></>;

  if (
    !hasAnyFilter &&
    Array.isArray(checkDocument) &&
    checkDocument.length === 0
  ) {
    return (
      <DocumentsEmptyStatusView
        managerIdOrSlug={props.manager?.id as ModelId}
      />
    );
  }

  return (
    <InvestorPortalWhiteLabelPageContainer
      px={0}
      className={"sm:py-10"}
      title={"Documents"}
    >
      <Box>
        <VeriTabs
          tabListWrapperProps={{ p: 0, maxW: "260px", w: "100%" }}
          tabListWrapperAs={Card}
          isVertical={!isMobile}
        >
          {categories.map((category) => (
            <VeriTabPanel key={category} label={String(category)}>
              {props.manager && (
                <DocumentsTable
                  form={form}
                  onClearFilters={onClearFilters}
                  startDownloadJob={startDownloadJob}
                  manager={props.manager}
                  tagId={availableTags?.[category] as ModelId}
                  hasAnyFilter={hasAnyFilter}
                />
              )}
            </VeriTabPanel>
          ))}
        </VeriTabs>
      </Box>
    </InvestorPortalWhiteLabelPageContainer>
  );
};

DocumentsPage.getInitialProps = async () => {
  const result: ManagerWhiteLabelRoutesServerInitialProps = {};
  return result;
};

DocumentsPage.getChunkName = () =>
  "investor-portal-3-manager-white-label-documents-page";
